import { MAX_UPLOAD_BYTES } from '@cult/domain';
import { z } from 'zod';
import { BaseEntitySchema } from './common';

/** Public path uploaded files are served from (behind the session guard, via the `/api` proxy prefix). */
export const UPLOADS_PUBLIC_PATH = '/api/uploads';

/** Storage keys are always `<uuid>.<ext>`, assigned by the API on upload. */
const UPLOAD_KEY_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\\.[a-z]+';
export const UPLOAD_URL_RE = new RegExp(`^${UPLOADS_PUBLIC_PATH}/(${UPLOAD_KEY_PATTERN})$`);

/** The storage key behind a CULT upload URL, or undefined for anything else (external links included). */
export const uploadKeyFromUrl = (url: string): string | undefined => UPLOAD_URL_RE.exec(url)?.[1];

// `mime` and `size` are informational only: the API stores the values it recorded at upload time.
// The url is checked by the API, not here — a newly added attachment must be the user's own CULT
// upload, but a url already on the record (even a legacy external one) may be sent back on edit.
export const AttachmentInputSchema = z.object({
  url: z.string().min(1).max(500),
  filename: z.string().trim().min(1).max(255),
  mime: z.string().min(1).max(120),
  size: z.number().int().nonnegative().max(MAX_UPLOAD_BYTES),
});
export type AttachmentInput = z.infer<typeof AttachmentInputSchema>;

const MAX_ATTACHMENTS_PER_RECORD = 10;

export const attachmentsInputField = z.array(AttachmentInputSchema).max(MAX_ATTACHMENTS_PER_RECORD).optional();

export const PublicAttachmentSchema = BaseEntitySchema.extend({
  url: z.string(),
  filename: z.string(),
  mime: z.string(),
  size: z.number().int().nonnegative(),
});
export type PublicAttachment = z.infer<typeof PublicAttachmentSchema>;

export const UploadResponseSchema = z.object({
  url: z.string().regex(UPLOAD_URL_RE),
  mime: z.string().min(1),
  size: z.number().int().nonnegative(),
});
export type UploadResponse = z.infer<typeof UploadResponseSchema>;
